import { Widget, WidgetDefinition, WidgetType } from '../types';

export type WidgetDimensions = {
  width: number;
  height: number;
};

type DefaultDimensionsType = {
  web: Partial<Record<WidgetType, WidgetDimensions>>;
  mobile: Partial<Record<WidgetType, WidgetDimensions>>;
};

/**
 * Per-device, per-type default span in GRID UNITS.
 *
 * Read by the auto-grid resolver (`WidgetLayout/autoGrid.ts`) to size a widget
 * that was authored without an explicit `width`/`height`. Note the map is
 * `Partial` and genuinely sparse (mobile has no `scatterChart`, no
 * `criticalStock`) — the resolver carries a hard fallback span for exactly that
 * reason. Never index it with a `!`.
 */
export const defaultDimensions: DefaultDimensionsType = {
  web: {
    areaChart: { width: 8, height: 6 },
    barChart: { width: 8, height: 6 },
    lineChart: { width: 8, height: 6 },
    pieChart: { width: 5, height: 5 },
    donutChart: { width: 5, height: 5 },
    scatterChart: { width: 8, height: 6 },
    // TV1-1933 (S18) — the critical-stock list is web-only (see webWidgetTypes).
    criticalStock: { width: 8, height: 6 },
    // TV1-1929 (S14) — the generic table is web-only too, and wants to be WIDE:
    // it renders one column per Cube member, and a pivot multiplies that.
    table: { width: 12, height: 8 },
    // TV1-1930 (S15) — a KPI is one number: SMALL. 5 wide tiles four abreast
    // across the 20-column web grid, which is what TV1-1932's tile row wants.
    // Height 5 (~207px), NOT 3: a KPI tile stacks title + big value + delta row
    // + caption (~155-166px), and cells have no gutter + `overflow: hidden`, so
    // height 3 (~124px) clipped the caption and butted tiles against the row
    // below — reading as widgets "on top of each other".
    kpi: { width: 5, height: 5 },
    // TV1-1930 (S15) — a text block is narrative: FULL width (a paragraph set in a
    // narrow column reads badly) and short. Web-only.
    text: { width: 20, height: 4 },
    // TV1-2291 (t21) — the two static structure widgets. WEB ONLY, like `text` and
    // `table`: they are absent from `mobileWidgetTypes`, so a mobile entry would be
    // unreachable data. (This map is deliberately `Partial` and sparse — the
    // auto-grid carries `FALLBACK_SPAN` for exactly that. Do NOT make it total; the
    // EXHAUSTIVE table is `MIN_CONTENT_PX`, and the two make opposite calls on
    // purpose.)
    //
    // A section divider spans the WHOLE grid — a rule that stops two thirds of the
    // way across does not read as a section break — and is only as tall as one line
    // of text plus air.
    heading: { width: 20, height: 2 },
    // A spacer's default is deliberately MODEST: a quarter of the grid, three rows.
    // It is the one widget whose size is the entire point, so the author will resize
    // it; the default only has to be big enough to grab.
    spacer: { width: 5, height: 3 },
    // TV1-2291 (t25) — the four READMODEL widgets, WEB ONLY for the same reason.
    //
    // `count` and `alert` take the KPI's span exactly (5x5, four abreast across the
    // 20-column grid): they replaced `KeyIndicators` / `AlertsSection` (deleted in
    // TV1-2343), which drew a row of tiles above and below the Cube-backed KPIs, and
    // a row whose tiles are not the same size does not read as a row. The curated
    // headline report places them at exactly this span for that reason.
    count: { width: 5, height: 5 },
    alert: { width: 5, height: 5 },
    // A progress bar is WIDE and SHORT — the bar's length is what carries the
    // proportion, and there is nothing below it but a caption.
    progress: { width: 8, height: 3 },
    // A list wants height (it is N rows) more than width (it is 2-3 fields), so it
    // is the mirror image of `progress`.
    list: { width: 8, height: 6 }
  },
  mobile: {
   areaChart: { width: 1, height: 1 },
    barChart: { width: 1, height: 1 },
    lineChart: { width: 1, height: 1 },
    pieChart: { width: 1, height: 1 },
    donutChart: { width: 1, height: 1 },
    scatterChart: { width: 1, height: 1 },
    // TV1-1930 (S15) — the KPI is the only NEW type that is mobile-capable, so
    // unlike `criticalStock`/`table` it genuinely needs a mobile entry. On the
    // 2-column mobile grid it takes one column and is half-height: a big number
    // needs far less room than a chart.
    kpi: { width: 1, height: 1 }
  }
};

/**
 * Create a POSITIONLESS widget (TV1-1931).
 *
 * The old `calculatePosition` stacked every new widget at `x: 0` below the
 * bottom-most one, and `defaultDimensions[layout.device!][widget.type]!` crashed
 * at runtime on any (device, type) pair the map doesn't carry. Both are gone:
 * placement and sizing are now the auto-grid's job
 * (`WidgetLayout/autoGrid.ts`). The resolver flows the new widget in below the
 * existing (pinned) ones, and `handleLayoutChange` materializes the whole
 * resolved layout before it is persisted — so the widget still reaches storage
 * with concrete coordinates, just not ones computed here.
 */
export const createWidget = ( widget: WidgetDefinition ): Widget => ({
  definition: { ...widget }
});
